import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatMoney, type CheckoutResponse, type QuoteDTO } from '@va/shared';
import { api, errorMessage } from '../api/client';
import { IconCard, IconClose, IconPaypal, IconShield, IconBolt } from '../components/icons';
import { Empty, ErrorBox, Loading } from '../components/ui';
import { desktop, isDesktop, mediaUrl, openExternal } from '../platform';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import { useToast } from '../state/toast';

type Provider = 'stripe' | 'paypal' | 'mock';

export function CartPage() {
  const cart = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [couponInput, setCouponInput] = useState(cart.coupon);
  const [provider, setProvider] = useState<Provider | null>(null);
  const [busy, setBusy] = useState(false);

  const quote = useQuery({
    queryKey: ['quote', cart.ids, cart.coupon, user?.id ?? null],
    queryFn: () => api.post<QuoteDTO>('/checkout/quote', { productIds: cart.ids, couponCode: cart.coupon || undefined }),
    enabled: cart.ids.length > 0,
  });
  const q = quote.data;

  // Drop products that are no longer for sale.
  useEffect(() => {
    if (!q) return;
    const valid = new Set(q.lines.map((l) => l.productId));
    cart.ids.filter((id) => !valid.has(id)).forEach((id) => cart.remove(id));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start on a payment method this buyer may use (one provider can be in test mode while another is live).
  useEffect(() => {
    if (!q || provider) return;
    const offered = (['stripe', 'paypal', 'mock'] as const).filter((p) => q.providers[p]);
    setProvider(offered.find((p) => q.mayPay?.[p] !== false) ?? offered[0] ?? null);
  }, [q, provider]);

  if (cart.ids.length === 0) {
    return (
      <div className="container">
        <div className="page-head"><h1>Your cart</h1></div>
        <Empty title="Your cart is empty" action={<Link className="btn btn-primary" to="/">Browse the store</Link>}>
          Every plugin can be tried free in the app before you buy.
        </Empty>
      </div>
    );
  }

  const payable = q ? q.lines.filter((l) => !l.alreadyOwned) : [];
  const anyProvider = q && (q.providers.stripe || q.providers.paypal || q.providers.mock);
  // A method this buyer can't use yet (still in test mode while another is live) is left out, as
  // long as another one works; only when none does, "Purchases open soon" explains why.
  const offeredMethods = q ? (['stripe', 'paypal', 'mock'] as const).filter((p) => q.providers[p]) : [];
  const payableMethods = offeredMethods.filter((p) => q?.mayPay?.[p] !== false);
  const shows = (p: 'stripe' | 'paypal' | 'mock') => (payableMethods.length ? payableMethods : offeredMethods).includes(p);
  // While payments run in test mode, the store may only let admins pay (anyone knows the test cards).
  const paying = !!q && q.totalCents > 0 && !!provider;
  const blocked = paying && q?.mayPay?.[provider!] === false;
  const testPayment = paying && !blocked && q?.testMode?.[provider!] === true;

  const checkout = async () => {
    if (!user) {
      nav('/login', { state: { from: '/cart' } });
      return;
    }
    if (!q) return;
    if (q.totalCents > 0 && !provider) return;
    setBusy(true);
    try {
      const res = await api.post<CheckoutResponse>('/checkout', {
        productIds: payable.map((l) => l.productId),
        provider: provider ?? 'stripe',
        couponCode: cart.coupon || undefined,
        source: isDesktop ? 'desktop' : 'web',
      });
      if (res.status === 'paid' || !res.redirectUrl) {
        cart.clear();
        nav(`/checkout/complete?order=${res.orderId}`);
        return;
      }
      if (isDesktop) {
        nav(`/checkout/complete?order=${res.orderId}&waiting=1`);
        // Pay in a window of the app (Stripe's or PayPal's own page); the browser is the fallback.
        const url = res.redirectUrl;
        if (desktop?.checkout) void desktop.checkout.open(url, res.orderId).catch(() => openExternal(url));
        else openExternal(url);
      } else {
        window.location.href = res.redirectUrl;
      }
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="page-head">
        <h1>Your cart</h1>
        <Link to="/" className="btn btn-ghost btn-sm">Continue shopping</Link>
      </div>
      {quote.error && <ErrorBox error={quote.error} onRetry={() => quote.refetch()} />}
      {quote.isLoading && <Loading />}
      {q && (
        <div className="cart-layout">
          <div className="card" style={{ padding: 0 }}>
            {q.lines.map((l) => (
              <div className="list-row" key={l.productId}>
                <Link to={`/products/${l.slug}`} className="list-thumb">{l.thumbnailUrl && <img src={mediaUrl(l.thumbnailUrl)} alt="" />}</Link>
                <div className="grow">
                  <Link to={`/products/${l.slug}`} style={{ color: 'var(--va-headline)', fontWeight: 600 }}>{l.name}</Link>
                  {l.alreadyOwned && <div className="small" style={{ color: 'var(--va-success)' }}>Already in My Library: it won't be charged.</div>}
                  {l.discountCents > 0 && <div className="small" style={{ color: 'var(--va-accent)' }}>−{formatMoney(l.discountCents, q.currency)} with {q.coupon?.code}</div>}
                </div>
                <span className="price">
                  {l.discountCents > 0 && <s>{formatMoney(l.unitPriceCents, q.currency)}</s>}
                  {l.unitPriceCents === 0 ? <span className="free">Free</span> : formatMoney(l.unitPriceCents - l.discountCents, q.currency)}
                </span>
                <button className="icon-btn" aria-label={`Remove ${l.name}`} onClick={() => cart.remove(l.productId)}>
                  <IconClose size={16} />
                </button>
              </div>
            ))}
          </div>

          <aside className="stack">
            <div className="card stack">
              <h3 style={{ margin: 0 }}>Summary</h3>
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  cart.setCoupon(couponInput.trim());
                }}
              >
                <input className="input mono" placeholder="Promo code" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} aria-label="Promo code" />
                <button className="btn btn-outline btn-sm" type="submit">Apply</button>
              </form>
              {q.couponError && cart.coupon && <div className="small" style={{ color: 'var(--va-danger)' }}>{q.couponError}</div>}
              {q.coupon && <div className="small" style={{ color: 'var(--va-success)' }}>{q.coupon.code}: {q.coupon.description}</div>}
              <div>
                <div className="summary-line"><span className="muted">Subtotal</span><span className="mono">{formatMoney(q.subtotalCents, q.currency)}</span></div>
                {q.discountCents > 0 && <div className="summary-line"><span className="muted">Discount</span><span className="mono" style={{ color: 'var(--va-accent)' }}>−{formatMoney(q.discountCents, q.currency)}</span></div>}
                <div className="summary-total"><span>Total</span><span>{formatMoney(q.totalCents, q.currency)}</span></div>
                <div className="small dim" style={{ marginTop: 6 }}>Prices in US dollars. No tax is added at checkout.</div>
              </div>

              {q.totalCents > 0 && (
                <div className="pay-options" role="radiogroup" aria-label="Payment method">
                  {shows('stripe') && (
                    <button type="button" role="radio" aria-checked={provider === 'stripe'} className={`pay-option${provider === 'stripe' ? ' on' : ''}`} onClick={() => setProvider('stripe')}>
                      <span className="radio" />
                      <IconCard size={20} />
                      <span>
                        <span className="pm-name">Credit or debit card</span>
                        <br />
                        <span className="small dim">Visa, Mastercard, Amex and more · secure checkout by Stripe</span>
                      </span>
                    </button>
                  )}
                  {shows('paypal') && (
                    <button type="button" role="radio" aria-checked={provider === 'paypal'} className={`pay-option${provider === 'paypal' ? ' on' : ''}`} onClick={() => setProvider('paypal')}>
                      <span className="radio" />
                      <IconPaypal size={20} />
                      <span>
                        <span className="pm-name">PayPal</span>
                        <br />
                        <span className="small dim">PayPal balance, cards through PayPal, Pay Later</span>
                      </span>
                    </button>
                  )}
                  {shows('mock') && (
                    <button type="button" role="radio" aria-checked={provider === 'mock'} className={`pay-option${provider === 'mock' ? ' on' : ''}`} onClick={() => setProvider('mock')}>
                      <span className="radio" />
                      <IconBolt size={20} />
                      <span>
                        <span className="pm-name">Test payment</span>
                        <br />
                        <span className="small dim">Development only, no real money</span>
                      </span>
                    </button>
                  )}
                  {!anyProvider && <div className="notice warning small">Checkout isn't available right now. Please try again later.</div>}
                </div>
              )}

              {blocked && (
                <div className="notice small">
                  <strong>Purchases open soon.</strong> Until then you can try every plugin free in the app, with the watermark.
                </div>
              )}
              {testPayment && (
                <div className="notice warning small">
                  {provider === 'stripe' && <>Test mode: no real money is charged. Card <span className="mono">4242 4242 4242 4242</span>, any future date, any CVC.</>}
                  {provider === 'paypal' && <>Test mode: no real money is charged. Sign in with a PayPal sandbox test buyer.</>}
                  {provider === 'mock' && <>Test mode: no real money is charged.</>}
                </div>
              )}

              <button className="btn btn-primary btn-lg btn-block" onClick={checkout} disabled={busy || blocked || payable.length === 0 || (q.totalCents > 0 && !provider)}>
                {busy ? 'Starting checkout…' : blocked ? 'Purchases open soon' : !user ? 'Sign in to check out' : q.totalCents === 0 ? 'Get it free' : `Pay ${formatMoney(q.totalCents, q.currency)}`}
              </button>
              <div className="row small dim" style={{ gap: 8 }}>
                <IconShield size={15} /> Licenses are added to your account right after payment.
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
